import { clamp01 } from '../math';

/**
 * Editable response curves.
 *
 * Used wherever a parameter should map one quantity onto another non-linearly —
 * line width over velocity, dot size over coverage, radius over distance to the text.
 * A curve is a list of control points in the unit square, sorted by x, with the first
 * at x = 0 and the last at x = 1.
 */
export interface CurvePoint {
  x: number;
  y: number;
}

export type Curve = CurvePoint[];

export const LINEAR_CURVE: Curve = [
  { x: 0, y: 0 },
  { x: 1, y: 1 },
];

export const EASE_OUT_CURVE: Curve = [
  { x: 0, y: 0 },
  { x: 0.35, y: 0.72 },
  { x: 1, y: 1 },
];

export const EASE_IN_CURVE: Curve = [
  { x: 0, y: 0 },
  { x: 0.65, y: 0.28 },
  { x: 1, y: 1 },
];

/** Rises then falls — the shape a tapering stroke wants. */
export const ARCH_CURVE: Curve = [
  { x: 0, y: 0 },
  { x: 0.5, y: 1 },
  { x: 1, y: 0 },
];

export const CURVE_PRESETS: { id: string; label: string; curve: Curve }[] = [
  { id: 'linear', label: 'Linear', curve: LINEAR_CURVE },
  { id: 'easeOut', label: 'Schnell → langsam', curve: EASE_OUT_CURVE },
  { id: 'easeIn', label: 'Langsam → schnell', curve: EASE_IN_CURVE },
  { id: 'arch', label: 'Bogen', curve: ARCH_CURVE },
];

export function cloneCurve(curve: Curve): Curve {
  return curve.map((p) => ({ x: p.x, y: p.y }));
}

/** Keeps the invariants the evaluator relies on: sorted, endpoints pinned, in range. */
export function normaliseCurve(curve: Curve): Curve {
  const points = curve
    .map((p) => ({ x: clamp01(p.x), y: clamp01(p.y) }))
    .sort((a, b) => a.x - b.x);
  if (points.length === 0) return cloneCurve(LINEAR_CURVE);
  points[0].x = 0;
  points[points.length - 1].x = 1;
  return points;
}

/**
 * Evaluates the curve at `t` using monotone cubic Hermite interpolation
 * (Fritsch & Carlson, "Monotone Piecewise Cubic Interpolation", SIAM 1980).
 *
 * Plain Catmull-Rom would overshoot outside [0, 1] between close control points,
 * which for a line-width or radius mapping means negative widths and inverted shapes.
 * The Fritsch-Carlson limiter guarantees the interpolant stays monotone between
 * samples, so the curve does exactly what its drawn shape promises.
 */
export function evalCurve(curve: Curve, t: number): number {
  const n = curve.length;
  if (n === 0) return clamp01(t);
  if (n === 1) return curve[0].y;

  const x = clamp01(t);
  if (x <= curve[0].x) return curve[0].y;
  if (x >= curve[n - 1].x) return curve[n - 1].y;

  let i = 0;
  while (i < n - 2 && curve[i + 1].x < x) i++;

  const p0 = curve[i];
  const p1 = curve[i + 1];
  const h = p1.x - p0.x;
  if (h <= 1e-9) return p1.y;

  // Secant slopes of the neighbouring intervals.
  const delta = (p1.y - p0.y) / h;
  const deltaPrev = i > 0 ? (p0.y - curve[i - 1].y) / (p0.x - curve[i - 1].x) : delta;
  const deltaNext = i < n - 2 ? (curve[i + 2].y - p1.y) / (curve[i + 2].x - p1.x) : delta;

  // Tangents: zero at local extrema, otherwise the average of adjacent secants,
  // then clamped to three times the smaller secant to preserve monotonicity.
  const tangent = (a: number, b: number): number => {
    if (a * b <= 0) return 0;
    const m = (a + b) / 2;
    const limit = 3 * Math.min(Math.abs(a), Math.abs(b));
    return Math.sign(m) * Math.min(Math.abs(m), limit);
  };
  const m0 = i === 0 ? delta : tangent(deltaPrev, delta);
  const m1 = i === n - 2 ? delta : tangent(delta, deltaNext);

  const s = (x - p0.x) / h;
  const s2 = s * s;
  const s3 = s2 * s;
  return (
    (2 * s3 - 3 * s2 + 1) * p0.y +
    (s3 - 2 * s2 + s) * h * m0 +
    (-2 * s3 + 3 * s2) * p1.y +
    (s3 - s2) * h * m1
  );
}

/** Pre-samples a curve into a lookup table — worth it inside per-particle loops. */
export function bakeCurve(curve: Curve, resolution = 256): Float32Array {
  const table = new Float32Array(resolution);
  for (let i = 0; i < resolution; i++) table[i] = evalCurve(curve, i / (resolution - 1));
  return table;
}

export function sampleBaked(table: Float32Array, t: number): number {
  const x = clamp01(t) * (table.length - 1);
  const i = x | 0;
  const j = i < table.length - 1 ? i + 1 : i;
  const f = x - i;
  return table[i] + (table[j] - table[i]) * f;
}
